import { ReactNode } from "react";

interface SubdomainConfig {
  subdomain: string;
  component: ReactNode;
}

interface SubdomainRouterProps {
  subdomains: SubdomainConfig[];
  main: ReactNode;
}

/**
 * Get the current subdomain from the hostname
 * Handles:
 * - blog.devdotcom.org -> "blog"
 * - blog.localhost -> "blog"
 * - www.devdotcom.org -> "www" (treated as main)
 * - devdotcom.org -> "" (main)
 * - localhost -> "" (main)
 */
export function getSubdomain(): string {
  const hostname = window.location.hostname;

  // Handle localhost (no subdomain)
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "";
  }

  // Handle blog.localhost or any subdomain.localhost
  if (hostname.endsWith(".localhost")) {
    return hostname.replace(".localhost", "").toLowerCase();
  }

  // Handle production domains
  const parts = hostname.split(".");

  // For domains like blog.devdotcom.org
  if (parts.length >= 3) {
    const subdomain = parts[0].toLowerCase();
    // Treat "www" as main domain, not a subdomain
    if (subdomain === "www") {
      return "";
    }
    return subdomain;
  }

  // No subdomain (e.g., devdotcom.org)
  return "";
}

/**
 * Check if we're on a specific subdomain
 */
export function isSubdomain(subdomain: string): boolean {
  return getSubdomain() === subdomain.toLowerCase();
}

/**
 * Get the base URL for a specific subdomain
 */
export function getSubdomainUrl(subdomain: string, path: string = ""): string {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;

  // Development mode - use subdomain.localhost
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    const portStr = port ? `:${port}` : "";
    return `${protocol}//${subdomain}.localhost${portStr}${path}`;
  }

  // Already on a subdomain.localhost
  if (hostname.endsWith(".localhost")) {
    const portStr = port ? `:${port}` : "";
    return `${protocol}//${subdomain}.localhost${portStr}${path}`;
  }

  // Production mode
  const parts = hostname.split(".");
  let baseDomain: string;

  if (parts.length >= 3 && parts[0] !== "www") {
    // Already on a subdomain, get base domain
    baseDomain = parts.slice(1).join(".");
  } else if (parts.length >= 3 && parts[0] === "www") {
    // On www subdomain
    baseDomain = parts.slice(1).join(".");
  } else {
    // On root domain
    baseDomain = hostname;
  }

  return `${protocol}//${subdomain}.${baseDomain}${path}`;
}

/**
 * Get the main domain URL
 */
export function getMainDomainUrl(path: string = ""): string {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;

  // Development mode - go back to localhost
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    const portStr = port ? `:${port}` : "";
    return `${protocol}//${hostname}${portStr}${path}`;
  }

  // Handle subdomain.localhost - go back to localhost
  if (hostname.endsWith(".localhost")) {
    const portStr = port ? `:${port}` : "";
    return `${protocol}//localhost${portStr}${path}`;
  }

  // Production mode
  const parts = hostname.split(".");
  let baseDomain: string;

  if (parts.length >= 3) {
    // On a subdomain, get base domain with www
    baseDomain = "www." + parts.slice(1).join(".");
  } else {
    // On root domain
    baseDomain = "www." + hostname;
  }

  return `${protocol}//${baseDomain}${path}`;
}

/**
 * Alias for getMainDomainUrl for clearer usage
 */
export function getMainPageUrl(path: string = ""): string {
  return getMainDomainUrl(path);
}

/**
 * SubdomainRouter component
 * Routes to different components based on subdomain
 */
export function SubdomainRouter({ subdomains, main }: SubdomainRouterProps) {
  const currentSubdomain = getSubdomain();

  // Find matching subdomain config
  const matchedSubdomain = subdomains.find(
    (config) => config.subdomain.toLowerCase() === currentSubdomain,
  );

  if (matchedSubdomain) {
    return <>{matchedSubdomain.component}</>;
  }

  return <>{main}</>;
}
